"use client";

import React from "react";
import { CircleCheck as CheckCircle, MapPin } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const projects = [{
  id: "proj-1",
  name: "Global Aerospace: Hangar Suppression",
  industry: "Aerospace",
  location: "Seattle, WA",
  scope: "Design and installation of high-expansion foam systems for wide-body aircraft hangars.",
  outcome: "Reduced potential fire damage footprint by 85% through rapid suppression.",
  imageSrc: "https://images.unsplash.com/photo-1540962351504-03099e0a754b?w=800&h=450&fit=crop&auto=format"
}, {
  id: "proj-3",
  name: "North Star Energy: LNG Terminal",
  industry: "Energy",
  location: "Houston, TX",
  scope: "Comprehensive fire risk assessment and mitigation strategy for a major LNG export hub.",
  outcome: "Achieved full compliance with NFPA 59A standards ahead of schedule.",
  imageSrc: "https://images.unsplash.com/photo-1516937941344-00b4e0337589?w=800&h=450&fit=crop&auto=format"
}, {
  id: "proj-5",
  name: "Titan Steel: Mill Protection",
  industry: "Industrial",
  location: "Pittsburgh, PA",
  scope: "High-temperature fire detection and deluge systems for heavy manufacturing lines.",
  outcome: "Successfully mitigated three localized flash fires without production stoppage.",
  imageSrc: "https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?w=800&h=450&fit=crop&auto=format"
}, {
  id: "proj-6",
  name: "Oceanic Logistics: Port Safety",
  industry: "Logistics",
  location: "Long Beach, CA",
  scope: "Automated fire monitoring for hazardous material container storage zones.",
  outcome: "Improved emergency response time by 40% through real-time sensor integration.",
  imageSrc: "https://images.unsplash.com/photo-1524522173746-f628baad3644?w=800&h=450&fit=crop&auto=format"
}, {
  id: "proj-7",
  name: "Summit Pharma: Cleanroom Safety",
  industry: "Pharmaceutical",
  location: "Basel, Switzerland",
  scope: "Specialized fire protection for ISO Class 5 cleanroom manufacturing suites.",
  outcome: "Maintained sterile environment integrity during system commissioning.",
  imageSrc: "https://images.unsplash.com/photo-1582719508461-905c673771fd?w=800&h=450&fit=crop&auto=format"
}, {
  id: "proj-8",
  name: "Vanguard Automotive: EV Plant",
  industry: "Automotive",
  location: "Austin, TX",
  scope: "Thermal runaway detection and suppression for lithium-ion battery assembly lines.",
  outcome: "Established industry-leading safety protocols for EV manufacturing.",
  imageSrc: "https://images.unsplash.com/photo-1581092160562-40aa08e78837?w=800&h=450&fit=crop&auto=format"
}, {
  id: "proj-10",
  name: "Skyline Towers: Mixed-Use Safety",
  industry: "Commercial",
  location: "Chicago, IL",
  scope: "Advanced life safety and smoke control systems for a 60-story skyscraper.",
  outcome: "Secured occupancy permit 3 months early due to seamless safety integration.",
  imageSrc: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&h=450&fit=crop&auto=format"
}, {
  id: "proj-11",
  name: "DeepBlue: Offshore Platform",
  industry: "Oil & Gas",
  location: "Gulf of Mexico",
  scope: "Corrosion-resistant firewater systems for deepwater drilling operations.",
  outcome: "Verified 100% system reliability in extreme maritime environments.",
  imageSrc: "https://images.unsplash.com/photo-1516937941344-00b4e0337589?w=800&h=450&fit=crop&auto=format"
}, {
  id: "proj-12",
  name: "Metro Rail: Tunnel Suppression",
  industry: "Infrastructure",
  location: "London, UK",
  scope: "Water mist fire suppression for high-traffic underground transit tunnels.",
  outcome: "Enhanced passenger safety while minimizing water damage to electrical systems.",
  imageSrc: "https://images.unsplash.com/photo-1515162305285-0293e4767cc2?w=800&h=450&fit=crop&auto=format"
}, {
  id: "proj-13",
  name: "Solaris: BESS Facility",
  industry: "Renewable Energy",
  location: "Adelaide, Australia",
  scope: "Fire risk management for utility-scale battery energy storage systems.",
  outcome: "Implemented early-warning gas detection to prevent thermal runaway.",
  imageSrc: "https://images.unsplash.com/photo-1508514177221-188b1cf16e9d?w=800&h=450&fit=crop&auto=format"
}, {
  id: "proj-14",
  name: "Heritage Museum: Archive Safety",
  industry: "Cultural",
  location: "Washington, D.C.",
  scope: "Inert gas fire suppression for rare document and artifact storage.",
  outcome: "Provided non-destructive protection for priceless historical assets.",
  imageSrc: "https://images.unsplash.com/photo-1503174971373-b1f69850bded?w=800&h=450&fit=crop&auto=format"
}, {
  id: "proj-15",
  name: "Arctic Mining: Remote Safety",
  industry: "Mining",
  location: "Nunavut, Canada",
  scope: "Low-temperature fire protection systems for remote arctic mining camps.",
  outcome: "Ensured life safety in environments reaching -40°C.",
  imageSrc: "https://images.unsplash.com/photo-1581092162384-8987c1d64718?w=800&h=450&fit=crop&auto=format"
}, {
  id: "proj-16",
  name: "Global Chem: Refinery Upgrade",
  industry: "Chemical",
  location: "Rotterdam, Netherlands",
  scope: "Modernization of fire detection and foam deluge for chemical storage tanks.",
  outcome: "Reduced insurance premiums by 15% through improved risk profile.",
  imageSrc: "https://images.unsplash.com/photo-1513828583688-c52646db42da?w=800&h=450&fit=crop&auto=format"
}, {
  id: "proj-17",
  name: "AeroEngine: Test Cell Safety",
  industry: "Aerospace",
  location: "Derby, UK",
  scope: "High-speed fire suppression for jet engine testing facilities.",
  outcome: "Protected multi-million dollar test assets from catastrophic fire loss.",
  imageSrc: "https://images.unsplash.com/photo-1517976487492-5750f3195933?w=800&h=450&fit=crop&auto=format"
}, {
  id: "proj-19",
  name: "IronWorks: Foundry Safety",
  industry: "Industrial",
  location: "Birmingham, AL",
  scope: "Infrared fire detection for molten metal processing areas.",
  outcome: "Eliminated false alarms while maintaining 24/7 fire watch.",
  imageSrc: "https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?w=800&h=450&fit=crop&auto=format"
}, {
  id: "proj-20",
  name: "Pacific Spirits: Distillery Safety",
  industry: "Food & Beverage",
  location: "Vancouver, Canada",
  scope: "Alcohol-resistant foam systems for high-proof spirit storage.",
  outcome: "Met stringent distillery safety codes while protecting product inventory.",
  imageSrc: "https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?w=800&h=450&fit=crop&auto=format"
}];
export default function ProjectsShowcase() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="projects-showcase" className="bg-background py-12 md:py-20 lg:py-28">
      <div ref={ref} className={`container mx-auto px-4 md:px-6 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <h2 className="font-default text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4 tracking-tight">
            Proven Results in Fire & Risk Management
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Explore our portfolio of successful projects across various industries, demonstrating our commitment to safety, compliance, and innovative engineering solutions.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {projects.map((project, i) => <Card key={project.id} data-index={i} className="overflow-hidden flex flex-col h-full border-border shadow-sm hover:shadow-lg transition-all duration-300 group bg-card pb-6">
              <div className="relative overflow-hidden">
                <AspectRatio ratio={16 / 9}>
                  <img src={project.imageSrc} data-wvc-srcset={`${project.imageSrc}-wvc-srcset`} data-wvc-sizes={`${project.imageSrc}-wvc-sizes`} alt={`Case study image for ${project.name}`} className="object-cover w-full h-full transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                </AspectRatio>
                <div className="absolute top-4 left-4">
                  <Badge variant="default" className="shadow-sm font-medium tracking-wide">
                    {project.industry}
                  </Badge>
                </div>
              </div>

              <CardHeader className="pb-4 pt-6">
                <CardTitle className="font-default text-xl leading-tight group-hover:text-primary transition-colors duration-300">
                  {project.name}
                </CardTitle>
                <div className="flex items-center text-muted-foreground text-sm mt-2.5">
                  <MapPin className="w-4 h-4 mr-1.5 shrink-0 text-primary/70" />
                  <span>{project.location}</span>
                </div>
              </CardHeader>

              <CardContent className="flex-grow flex flex-col gap-5">
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {project.scope}
                </p>
                
                <div className="mt-auto pt-2">
                  <div className="bg-primary/5 border border-primary/10 rounded-md p-3.5 flex items-start gap-3 transition-colors group-hover:bg-primary/10">
                    <CheckCircle className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                    <p className="text-sm text-foreground font-medium leading-snug">
                      {project.outcome}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>)}
        </div>
        
        <div className="mt-12 text-center">
          <Button size="lg" asChild>
            <Link to="/Contact">
              Discuss Your Next Project
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}